import { AfterViewInit, Component, ContentChildren, Input, NgZone, QueryList, ViewChild } from '@angular/core';
import { BaseViewComponent, IFrameScrollMessage } from '@sb/library/base';
import { TabGroupBodyComponent } from '@sb/library/tab';
import { StepComponent } from './step/step.component';

declare const window: Window & {
    parentIFrame: any;
};

@Component({
    selector: 'lib-stepper',
    templateUrl: './stepper.component.html',
    styleUrl: './stepper.component.scss'
})
export class StepperComponent<T> extends BaseViewComponent implements AfterViewInit {

    private _steps: StepComponent<T>[] = [];
    private _currentStepPosition: number = 0;
    private _stepHistory: T[] = [];
    private _stepHasBeenUpdated = false;

    @Input({required: true}) type!: T;

    constructor(private _NgZone: NgZone) {
        super();
    }

    ngAfterViewInit(): void {
        this.tabBody.selectedIndex = this._currentStepPosition;
        this._steps = this.stepQueryList.toArray();
        this._steps.forEach(step => step.stepper = this);
        this.stepQueryList.changes.subscribe((steps: QueryList<StepComponent<T>>) => {
            this._steps = steps.toArray();
            this._steps.forEach(step => step.stepper = this);
        });
    }

    Next(): void {
        if (this._currentStepPosition < this._steps.length) {
            this._currentStepPosition++;
            this.UpdateCurrentPosition();
        }
    }

    Previous(): void {
        if (this._currentStepPosition > 0) {
            this._currentStepPosition--;
            this.UpdateCurrentPosition();
        }
    }

    GoToStart(): void {
        this._currentStepPosition = 0;
        this.UpdateCurrentPosition();
    }

    GoToStep(name: T[keyof T]): void {
        const index = this._steps.findIndex(s => s.name === name);
        if (index === -1) {
            throw Error("Step could not be found");
        }
        this._currentStepPosition = index;
        this.UpdateCurrentPosition();
    }

    GetCurrentStep(): T[keyof T] {
        return this._steps[this._currentStepPosition].name as T[keyof T];
    }

    GetPreviousStep(): T[keyof T] {
        return this._stepHistory[this._stepHistory.length - 2] as T[keyof T];
    }

    private UpdateCurrentPosition(): void {
        this._NgZone.run(() => {
            this._stepHasBeenUpdated = true;
            this.tabBody.selectedIndex = this._currentStepPosition;
            const currentStep = this._steps[this._currentStepPosition];
            this._stepHistory.push(currentStep.name);
        })
    }

    AnimationDone(): void {
        const currentStep = this._steps[this._currentStepPosition];
        const element = document.getElementById(currentStep?.name as string);
        if (this._stepHasBeenUpdated && element) {
            if (!window.parentIFrame) {
                element?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            } else {
                const rect = element.getBoundingClientRect();
                window.parentIFrame.sendMessage(new IFrameScrollMessage({ OffsetTop: rect.top, OffsetBottom: rect.bottom }));
            }
        }
    }

    get currentStepPosition(): number {
        return this._currentStepPosition;
    }

    get steps(): StepComponent<T>[] {
        return this._steps;
    }

    get totalSteps(): number {
        return this._steps.length;
    }

    @ViewChild(TabGroupBodyComponent) tabBody!: TabGroupBodyComponent<any>;
    @ContentChildren(StepComponent<T>) stepQueryList!: QueryList<StepComponent<T>>;

}
